/**
 * GroupedOfferingSelector — "Vad erbjuder ni här?"
 *
 * Renders offering types grouped by offering_groups from the DB catalog.
 * The catalog is fetched from DB — no hardcoded constants as SoT.
 *
 * Each offering is presented as a clickable selectable card with icon
 * and optional microcopy. Contextual helpers (tips, legal info, CTAs)
 * are rendered inline directly under the card they belong to.
 */
import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Check,
  Lightbulb,
  Info,
  ArrowRight,
  Loader2,
  Bed,
  Coffee,
  Store,
  Package,
  GraduationCap,
  Map as MapIcon,
  Calendar,
  Tag,
  Leaf,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics';
import { useOfferingCatalog } from '@/hooks/useOfferingCatalog';
import type { OfferingGroupWithTypes, OfferingType } from '@/lib/producer/offeringTypes';

/* ─── Icon mapping per offering key ─── */

const OFFERING_ICONS: Record<string, LucideIcon> = {
  accommodation: Bed,
  food_fika: Coffee,
  farm_shop: Store,
  preorder_pickup: Package,
  other_farm_sales: Store,
  courses_workshops: GraduationCap,
  tours_guiding: MapIcon,
  events: Calendar,
  flea_market: Tag,
  animals_nature: Leaf,
};

const DEFAULT_ICON: LucideIcon = Sparkles;

/* ─── Microcopy per offering key ─── */

const OFFERING_MICROCOPY: Record<string, string> = {
  accommodation: 'Möjlighet att övernatta',
  food_fika: 'Servering av mat eller fika',
  farm_shop: 'Sälj direkt från gården',
  preorder_pickup: 'Förbeställ och hämta upp',
  other_farm_sales: 'Gårdsförsäljning av t.ex. dryck',
  courses_workshops: 'Lär ut och skapa bokningsbara tillfällen',
  tours_guiding: 'Guidningar och upplevelser',
  events: 'Säsongsöppningar, festivaler och temadagar',
  flea_market: 'Återbruk och försäljning vid tillfällen',
  animals_nature: 'Djurupplevelser och naturaktiviteter',
};

/* ─── Inline contextual helper ─── */

function InlineHelper({
  icon: Icon,
  children,
}: {
  icon: typeof Info;
  children: ReactNode;
}) {
  return (
    <div className="mt-0.5 flex gap-2 rounded-md bg-muted/30 px-3 py-2">
      <Icon className="h-3.5 w-3.5 mt-[2px] shrink-0 text-muted-foreground/70" />
      <div className="text-xs text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}

/* ─── Selectable offering card ─── */

function OfferingCard({
  type,
  selected,
  onToggle,
}: {
  type: OfferingType;
  selected: boolean;
  onToggle: () => void;
}) {
  const Icon = OFFERING_ICONS[type.key] ?? DEFAULT_ICON;
  const microcopy = OFFERING_MICROCOPY[type.key] ?? type.short_description_sv;

  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'relative flex items-start gap-3 w-full rounded-xl border px-4 py-3.5 text-left transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 focus-visible:ring-offset-1',
        selected
          ? 'border-primary bg-primary/5 shadow-sm'
          : 'border-border bg-card hover:border-foreground/20 hover:bg-accent/30',
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors',
          selected
            ? 'bg-primary/10 text-primary'
            : 'bg-muted text-muted-foreground',
        )}
      >
        <Icon className="h-4 w-4" />
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <span
          className={cn(
            'text-sm font-medium leading-tight',
            selected ? 'text-foreground' : 'text-foreground',
          )}
        >
          {type.label_sv}
        </span>
        {microcopy && (
          <p className="mt-0.5 text-xs text-muted-foreground leading-snug line-clamp-2">
            {microcopy}
          </p>
        )}
      </div>

      {/* Selected indicator */}
      {selected && (
        <div className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-3 w-3" strokeWidth={3} />
        </div>
      )}
    </button>
  );
}

/* ─── Props ─── */

interface GroupedOfferingSelectorProps {
  offerings: string[];
  onOfferingsChange: (offerings: string[]) => void;
  otherText?: string;
  onOtherTextChange?: (text: string) => void;
  showSummary?: boolean;
  producerContext?: {
    producerId: string;
    activeActivityCount: number;
    basePath: string;
  };
}

export function GroupedOfferingSelector({
  offerings,
  onOfferingsChange,
  otherText = '',
  onOtherTextChange,
  showSummary = true,
  producerContext,
}: GroupedOfferingSelectorProps) {
  const { groups, isLoading } = useOfferingCatalog();
  const [otherChecked, setOtherChecked] = useState(otherText.length > 0);
  const navigate = useNavigate();

  const toggleOffering = useCallback((key: string) => {
    const next = offerings.includes(key)
      ? offerings.filter((k) => k !== key)
      : [...offerings, key];
    onOfferingsChange(next);
  }, [offerings, onOfferingsChange]);

  const handleOtherToggle = useCallback((checked: boolean) => {
    setOtherChecked(checked);
    if (!checked) {
      onOtherTextChange?.('');
    }
  }, [onOtherTextChange]);

  const handleNextStepClick = useCallback((kind: 'create_activity', href: string) => {
    if (producerContext) {
      track('offerings_next_step_clicked', {
        producer_id: producerContext.producerId,
        kind,
      });
    }
    navigate(href);
  }, [producerContext, navigate]);

  /* Build inline helpers map: offering key → ReactNode rendered under that option */
  const inlineHelpers: Record<string, ReactNode> = {};

  if (offerings.includes('other_farm_sales')) {
    inlineHelpers['other_farm_sales'] = (
      <>
        <InlineHelper icon={Lightbulb}>
          Många värdar kopplar gårdsförsäljning till en provning eller rundtur.
          Skapa gärna en aktivitet under "Aktiviteter".
        </InlineHelper>
        <InlineHelper icon={Info}>
          Gårdsförsäljning sker enligt svensk lag i samband med besök hos producenten
          och kräver kommunalt tillstånd. Farmly säljer inte alkohol.
        </InlineHelper>
        {producerContext && producerContext.activeActivityCount === 0 && (
          <div className="mt-0.5 flex items-start gap-2 rounded-md bg-primary/5 border border-primary/10 px-3 py-2">
            <ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Nästa steg:</span>{' '}
              Gårdsförsäljning sker oftast i samband med provning, rundtur eller besök.
              <Button
                variant="outline"
                size="sm"
                className="mt-1.5 h-6 text-[11px] gap-1"
                onClick={() => handleNextStepClick('create_activity', `${producerContext.basePath}/events/new`)}
              >
                Skapa aktivitet
                <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (offerings.includes('accommodation')) {
    inlineHelpers['accommodation'] = (
      <InlineHelper icon={Info}>
        Boende visas på din sida som en signal om att det finns möjlighet att övernatta.
        Besökare kontaktar dig direkt för mer information.
      </InlineHelper>
    );
  }

  if (offerings.includes('flea_market')) {
    inlineHelpers['flea_market'] = (
      <>
        <InlineHelper icon={Info}>
          Loppis visas som ett eget tillfälle med datum, plats och ev. kategorier.
          Du kan lägga till flera tillfällen under samma loppis.
        </InlineHelper>
        {producerContext && (
           <div className="mt-0.5 flex items-start gap-2 rounded-md bg-primary/5 border border-primary/10 px-3 py-2">
            <ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Nästa steg:</span>{' '}
              Skapa din första loppis med datum och detaljer.
              <Button
                variant="outline"
                size="sm"
                className="mt-1.5 h-6 text-[11px] gap-1"
                onClick={() => handleNextStepClick('create_activity', `${producerContext.basePath}/loppis`)}
              >
                Skapa loppis
                <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (offerings.includes('courses_workshops')) {
    inlineHelpers['courses_workshops'] = (
      <>
        <InlineHelper icon={Lightbulb}>
          Kurser och workshops visas som bokningsbara aktiviteter.
          Besökare kan se datum, pris och anmäla sig direkt.
        </InlineHelper>
        {producerContext && producerContext.activeActivityCount === 0 && (
           <div className="mt-0.5 flex items-start gap-2 rounded-md bg-primary/5 border border-primary/10 px-3 py-2">
            <ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Nästa steg:</span>{' '}
              Skapa din första kurs eller workshop som aktivitet.
              <Button
                variant="outline"
                size="sm"
                className="mt-1.5 h-6 text-[11px] gap-1"
                onClick={() => handleNextStepClick('create_activity', `${producerContext.basePath}/events/new`)}
              >
                Skapa aktivitet
                <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (offerings.includes('events')) {
    inlineHelpers['events'] = (
      <>
        <InlineHelper icon={Lightbulb}>
          Evenemang visas som aktiviteter med datum och plats.
          Perfekt för säsongsöppningar, festivaler eller temadagar.
        </InlineHelper>
        {producerContext && producerContext.activeActivityCount === 0 && (
           <div className="mt-0.5 flex items-start gap-2 rounded-md bg-primary/5 border border-primary/10 px-3 py-2">
            <ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Nästa steg:</span>{' '}
              Skapa ditt första evenemang som aktivitet.
              <Button
                variant="outline"
                size="sm"
                className="mt-1.5 h-6 text-[11px] gap-1"
                onClick={() => handleNextStepClick('create_activity', `${producerContext.basePath}/events/new`)}
              >
                Skapa aktivitet
                <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (offerings.includes('tours_guiding')) {
    inlineHelpers['tours_guiding'] = (
      <>
        <InlineHelper icon={Lightbulb}>
          Guidningar och turer visas som aktiviteter som besökare kan boka.
          Beskriv vad besökaren får uppleva och hur lång turen är.
        </InlineHelper>
        {producerContext && producerContext.activeActivityCount === 0 && (
          <div className="mt-0.5 flex items-start gap-2 rounded-md bg-primary/5 border border-primary/10 px-3 py-2">
            <ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-medium text-foreground">Nästa steg:</span>{' '}
              Skapa din första guidning eller tur som aktivitet.
              <Button
                variant="outline"
                size="sm"
                className="mt-1.5 h-6 text-[11px] gap-1"
                onClick={() => handleNextStepClick('create_activity', `${producerContext.basePath}/events/new`)}
              >
                Skapa aktivitet
                <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
          </div>
        )}
      </>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Laddar…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Grouped offerings */}
      {groups.map((group) => (
        <OfferingGroupBlock
          key={group.key}
          group={group}
          selectedKeys={offerings}
          onToggle={toggleOffering}
          inlineHelpers={inlineHelpers}
        />
      ))}

      {/* "Något annat" free-text as a card */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => handleOtherToggle(!otherChecked)}
          className={cn(
            'relative flex items-start gap-3 w-full rounded-xl border px-4 py-3.5 text-left transition-all duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 focus-visible:ring-offset-1',
            otherChecked
              ? 'border-primary bg-primary/5 shadow-sm'
              : 'border-border bg-card hover:border-foreground/20 hover:bg-accent/30',
          )}
        >
          <div
            className={cn(
              'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors',
              otherChecked
                ? 'bg-primary/10 text-primary'
                : 'bg-muted text-muted-foreground',
            )}
          >
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-foreground">Något annat</span>
            <p className="mt-0.5 text-xs text-muted-foreground leading-snug">
              Beskriv vad ni erbjuder
            </p>
          </div>
          {otherChecked && (
            <div className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Check className="h-3 w-3" strokeWidth={3} />
            </div>
          )}
        </button>
        {otherChecked && (
          <div className="mt-1 ml-7 mr-2 space-y-1">
            <p className="text-xs text-muted-foreground">
              Beskriv kort (valfritt)
            </p>
            <Input
              value={otherText}
              onChange={(e) => onOtherTextChange?.(e.target.value)}
              placeholder="t.ex. Bastu, kanotuthyrning…"
              className="max-w-xs"
              maxLength={200}
            />
            <p className="text-[11px] text-muted-foreground/60">
              Saknas något? Berätta gärna så kan vi lägga till det i Farmly.
            </p>
          </div>
        )}
      </div>

      {/* Summary */}
      {showSummary && offerings.length > 0 && (
        <p className="text-xs text-primary inline-flex items-center gap-1">
          <Check className="h-3 w-3" />
          {offerings.length} {offerings.length === 1 ? 'val gjort' : 'val gjorda'}
        </p>
      )}
    </div>
  );
}

/* ─── Group block ─── */

function OfferingGroupBlock({
  group,
  selectedKeys,
  onToggle,
  inlineHelpers,
}: {
  group: OfferingGroupWithTypes;
  selectedKeys: string[];
  onToggle: (key: string) => void;
  inlineHelpers: Record<string, ReactNode>;
}) {
  const selectedCount = group.types.filter((t) => selectedKeys.includes(t.key)).length;

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-medium text-foreground">{group.label_sv}</h4>
        {selectedCount > 0 && (
          <span className="text-[11px] text-primary font-medium">
            {selectedCount} vald{selectedCount > 1 ? 'a' : ''}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {group.types.map((type) => {
          const isSelected = selectedKeys.includes(type.key);
          const hasHelper = isSelected && inlineHelpers[type.key];

          return (
            <div
              key={type.key}
              className={cn(
                // When a helper is shown, span full width so helper stays visually attached
                hasHelper && 'sm:col-span-2',
              )}
            >
              <OfferingCard
                type={type}
                selected={isSelected}
                onToggle={() => onToggle(type.key)}
              />
              {/* Inline contextual helper rendered directly under its card */}
              {hasHelper && (
                <div className="mt-1 ml-7 mr-2 space-y-0.5">
                  {inlineHelpers[type.key]}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Build a hint string for accordion collapsed state */
export function buildGroupedOfferHint(offerings: string[], groups: OfferingGroupWithTypes[]): string {
  if (offerings.length === 0) return 'Välj vad ni erbjuder';
  const allTypes = groups.flatMap((g) => g.types);
  const labelMap = new Map(allTypes.map((t) => [t.key, t.label_sv]));
  const labels = offerings.map((k) => labelMap.get(k) ?? k);
  return `✔ ${offerings.length} valda · ${labels.slice(0, 4).join(' · ')}${labels.length > 4 ? ' …' : ''}`;
}
